import { transient, computedFrom } from "aurelia-framework";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { LookupValue, LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { VoyageDataSubmissionStatusEnum } from "../../../../../../models/vetting/vetting.models";
import {
    NotificationClientService,
    NotificationType
} from "../../../../../../services/basics/notification-client.service";
import { MultiSelectLookupControl } from "../../../../../components/lookups/multi-select-lookup-control/multi-select-lookup-control";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { VoyageDataSubmissionService } from "../../../../../../services/vetting/voyage-data-submission.service";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Messages } from "../../../../../../configuration/event-messages.constants";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { NumberFormatHelper } from "../../../../../../helpers/number-format-helper";
import { CultureInfo } from "../../../../../../models/internationalization/culture-info.models";
import { CultureInfoService } from "../../../../../../services/internationalization/culture-info.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { Reflection } from "../../../../../../utilities/reflection";

@transient()
export class VoyageDataSubmissionStatusDialog {

    subtaskTypesVM: MultiSelectLookupControl;
    setVoyageDataSubmissionStatus: any;
    readonly awaitingResponse: number = VoyageDataSubmissionStatusEnum.AwaitingResponse;
    readonly completed: number = VoyageDataSubmissionStatusEnum.Completed;
    readonly readyForReporting: number = VoyageDataSubmissionStatusEnum.ReadyForReporting;
    readonly inProgress: number = VoyageDataSubmissionStatusEnum.InProgress;
    readonly cancelled: number = VoyageDataSubmissionStatusEnum.Cancelled;
    onSubmit: (voyageDataSubmissionHasAnOutcome: boolean) => void;

    voyageDataSubmissionId: number;
    ghgIntensityCalculation: number;
    emissionsCalculation: number;
    comments: string;
    otherComments: string;
    refreshVoyageDataSubmission: any;
    statusSnippet: string;
    subtaskTypes: number[] = [];
    attachments: File[] = [];
    dataAttachments: File[] = [];
    otherAttachments: File[] = [];
    meta: any = {};
    isDropdownOpen: boolean;

    voyageDataSubmissionStatusId: number;
    voyageDataSubmissionStatuses: LookupValue[] = [] ;
    currentVoyageDataSubmissionStatus: LookupValue;
    lastSelectedItemIds: number[] = [];
    reopenStatusReasonId: number;
    showGhgIntensity: boolean = false;
    isNotReadyForReporting: boolean = false;
    isEmissionsFieldReadonly: boolean = true;
    isGhgIntensityFieldReadonly: boolean = true;

    @computedFrom('voyageDataSubmissionStatusId')
    get wasClosed() {
        const statuses = [
            VoyageDataSubmissionStatusEnum.ReadyForReporting,
            VoyageDataSubmissionStatusEnum.Completed,
            VoyageDataSubmissionStatusEnum.Cancelled
        ];

        return statuses.indexOf(this.voyageDataSubmissionStatusId) != -1;
    }

    @computedFrom('attachments', 'attachments.length')
    get isAttachmentAdded() {
        return this.attachments.length >= 1;
    }

    @computedFrom('dataAttachments', 'dataAttachments.length')
    get isDataAttachmentAdded() {
        return this.dataAttachments.length >= 1;
    }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.currentVoyageDataSubmissionStatus),
        Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.currentVoyageDataSubmissionStatus.id))
    get commentsLabel() {
        if (this.currentVoyageDataSubmissionStatus) {
            switch (this.currentVoyageDataSubmissionStatus.id) {
                case VoyageDataSubmissionStatusEnum.ReadyForReporting:
                case VoyageDataSubmissionStatusEnum.Completed:
                case VoyageDataSubmissionStatusEnum.Cancelled:
                    return "Final Comments";
            }
        }

        return "Comments"
    }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.cultureInfoService.cultures),
    Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.cultureInfoService.cultures.length))
    public get cultures(): CultureInfo[] {
        return this.cultureInfoService.cultures;
    }

    public culture: CultureInfo;

    constructor(private lookupClientService: LookupClientService, private dialogClientService: DialogClientService, private notificationsClientService: NotificationClientService,
        private voyageDataSubmissionClientService: VoyageDataSubmissionService, private validationController: ValidationController, private eventAggregator: EventAggregator,
        private validationsClientService: ValidationsClientService,
        private cultureInfoService: CultureInfoService,
        private userService: UserClientService, private userClaims: UserClaimsService) {
        this.showGhgIntensity = this.userClaims.hasPermission(Claims.ShowGhgIntensity);
        this.validationController.addRenderer(new CustomValidationRenderer());
        ValidationRules
            .ensure('reopenStatusReasonId')
            .required()
            .withMessage("Reason for status change is required")
            .when(() => this.currentVoyageDataSubmissionStatus.id == VoyageDataSubmissionStatusEnum.InProgress && this.wasClosed)
            .on(this);

    }

    subtaskTypesLoaded($event) {
        if (this.subtaskTypesVM && $event && $event.detail && $event.detail.values) {
            this.selectedItemsChanged({
                detail: $event.detail.values
            });
        }
    }

    async activate(model) {
        this.onSubmit = model.onSubmit;
        this.refreshVoyageDataSubmission = model.refreshVoyageDataSubmission;
        this.otherComments = model.otherComments;
        this.voyageDataSubmissionId = model.voyageDataSubmissionId;
        this.voyageDataSubmissionStatusId = model.voyageDataSubmissionStatusId;
        this.setVoyageDataSubmissionStatus = model.setVoyageDataSubmissionStatus;

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VoyageDataSubmissionStatuses;
        const response = await this.lookupClientService.getLookup(request);
        this.voyageDataSubmissionStatuses = response.activeValues;
        if (this.voyageDataSubmissionStatusId == VoyageDataSubmissionStatusEnum.ReportingComplete) {
            this.voyageDataSubmissionStatuses = this.voyageDataSubmissionStatuses.filter(x => x.id == VoyageDataSubmissionStatusEnum.Completed  || x.id == VoyageDataSubmissionStatusEnum.InProgress || x.id == this.voyageDataSubmissionStatusId);
        } else if (this.wasClosed) {
            this.voyageDataSubmissionStatuses = this.voyageDataSubmissionStatuses.filter(x => x.id == VoyageDataSubmissionStatusEnum.InProgress || x.id == this.voyageDataSubmissionStatusId);
        }
       
        this.reopenStatusReasonId = null;
        this.currentVoyageDataSubmissionStatus = this.voyageDataSubmissionStatuses.filter(x => x.id == (this.wasClosed ? VoyageDataSubmissionStatusEnum.InProgress : this.voyageDataSubmissionStatusId))[0];
        this.subtaskTypes = model.subtaskTypes;
        this.culture = this.userService.getCurrentUserCulture();
    }

    openDropdown(event) {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    checkIfIsReadyForReporting() {
        return new Promise<void>((resolve) => {
            this.voyageDataSubmissionClientService.getIsNotReadyForReporting(this.voyageDataSubmissionId)
            .then(response => {
                this.isNotReadyForReporting = response;
                resolve();
            }).catch(() => {
                this.notificationsClientService.show("Error checking if voyage is ready for reporting", "", NotificationType.Error);
                resolve();
            });
        })
    }

    closeDropdown() {
        this.isDropdownOpen = false;
    }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.currentVoyageDataSubmissionStatus),
        Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.voyageDataSubmissionStatusId),
        Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.currentVoyageDataSubmissionStatus.id))
    get submitEnabled(): boolean {
        return this.currentVoyageDataSubmissionStatus && this.voyageDataSubmissionStatusId != this.currentVoyageDataSubmissionStatus.id;
    }

    changeVoyageDataSubmissionStatus(currentVoyageDataSubmissionStatus: LookupValue) {
        if (!this.currentVoyageDataSubmissionStatus || this.currentVoyageDataSubmissionStatus.id != currentVoyageDataSubmissionStatus.id)
            this.meta = {};
        
        if (currentVoyageDataSubmissionStatus.id == this.completed){
            this.checkIfIsReadyForReporting();
            
            this.voyageDataSubmissionClientService.getVoyageDataSubmissionTotalEmissionsAndIntensity(this.voyageDataSubmissionId).then(response => {
                if(response && response.totals) {
                    this.emissionsCalculation = response.totals.totalEmissions;
                    this.ghgIntensityCalculation = response.totals.totalIntensity;
                    
                    this.isEmissionsFieldReadonly = this.emissionsCalculation != null ? true : false;
                    this.isGhgIntensityFieldReadonly = this.ghgIntensityCalculation != null ? true : false;
                }
                else {
                    this.isEmissionsFieldReadonly = false;
                    this.isGhgIntensityFieldReadonly = false;
                }
            })
            .catch(() => {
                this.notificationsClientService.show("Error", "Could not get total emissions and total intensity for this voyage data submission", NotificationType.Error);
            });
        }

        this.currentVoyageDataSubmissionStatus = currentVoyageDataSubmissionStatus;

        this.comments = "";
        this.statusSnippet = "";
        this.reopenStatusReasonId = null;
    }

    cancel() {
        this.dialogClientService.hide();
    }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.emissionsCalculation),
        Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.trySubmit))
    get invalidCarbonEmissions(): boolean { return !this.emissionsCalculation && this.trySubmit; }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.ghgIntensityCalculation),
        Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.trySubmit),
        Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.showGhgIntensity))
    get invalidGhgIntensity(): boolean { return !this.ghgIntensityCalculation && this.trySubmit && this.showGhgIntensity; }

    @computedFrom(Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.dataAttachments),
    Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.dataAttachments.length),
        Reflection.getPropertyPath<VoyageDataSubmissionStatusDialog>(z => z.trySubmit))
    get dataAttachmentsRequired(): boolean { return this.dataAttachments.length <= 0 && this.trySubmit; }
    trySubmit: boolean = false;

    async submit() {

        if (this.isNotReadyForReporting) {
            this.notificationsClientService.show("Error", "This voyage cannot be completed because is not ready for reporting", NotificationType.Error);
                return;
        }

        if (this.currentVoyageDataSubmissionStatus.id == this.cancelled) {
            if (!this.comments || this.comments.trim() == "") {
                this.notificationsClientService.show("Error", "Final Comments are required", NotificationType.Error);
                return;
            }
            this.subtaskTypes = [];
            this.statusSnippet = this.comments;
        }

        if (this.currentVoyageDataSubmissionStatus.id == this.completed) {
            this.statusSnippet = "";
            if (this.ghgIntensityCalculation)
                this.statusSnippet += `GHG Intensity: ${NumberFormatHelper.getFormattedValue(this.ghgIntensityCalculation, null, 0)}`
            if (this.emissionsCalculation) {
                if (this.ghgIntensityCalculation) {
                    this.statusSnippet += '</br>'
                }

                this.statusSnippet += `Carbon Emissions: ${NumberFormatHelper.getFormattedValue(this.emissionsCalculation, null, 2)}`
            }
        }

        this.trySubmit = true;
        const validationResponse = await this.validationController.validate();

        if (!validationResponse.valid) return;

        if (this.currentVoyageDataSubmissionStatus.id == this.completed && (this.dataAttachmentsRequired || this.invalidGhgIntensity || this.invalidCarbonEmissions)) return;

        try {
            const meta = JSON.stringify(this.meta);
            await this.voyageDataSubmissionClientService.changeStatus(this.voyageDataSubmissionId, this.currentVoyageDataSubmissionStatus.id,
                this.comments, meta, this.subtaskTypes, this.statusSnippet, this.reopenStatusReasonId, this.attachments, this.dataAttachments, this.otherAttachments,
                this.emissionsCalculation, this.ghgIntensityCalculation,
                this.culture.code);

            if (this.onSubmit) {
                const hasAnOutcome = this.currentVoyageDataSubmissionStatus.id == VoyageDataSubmissionStatusEnum.ReadyForReporting
                    || this.currentVoyageDataSubmissionStatus.id == VoyageDataSubmissionStatusEnum.Completed
                    || this.currentVoyageDataSubmissionStatus.id == VoyageDataSubmissionStatusEnum.Cancelled;
                this.onSubmit(hasAnOutcome);
            }

            if (this.currentVoyageDataSubmissionStatus.id == this.completed && this.refreshVoyageDataSubmission) {
                this.refreshVoyageDataSubmission({ voyageDataSubmissionId: this.voyageDataSubmissionId });
            }

            this.dialogClientService.hide();
        } catch (e) {
            if (e && e.content && e.content.validationResult)
                this.validationsClientService.showValidationErrors(e.content.validationResult);
        }
    }

    subscription: Subscription;
    attached() {
        this.subscription = this.eventAggregator.subscribe(Messages.documentClick, () => { this.closeDropdown(); });
    }

    detached() {
        if (this.subscription) this.subscription.dispose();
    }

    selectedItemsChanged($event) {
        const notSelected = $event && $event.detail ? $event.detail.filter(x => !x.isSelected) : [];

        if (this.statusSnippet) {
            notSelected.forEach(x => {
                const index = this.lastSelectedItemIds.indexOf(x.id);
                if (index >= 0)
                    this.lastSelectedItemIds.splice(index, 1);
                this.statusSnippet = this.statusSnippet.split(`, ${x.name}`).join("");
                this.statusSnippet = this.statusSnippet.split(`${x.name}, `).join("");
                this.statusSnippet = this.statusSnippet.split(x.name).join("");
            });
        }

        const newItems = $event && $event.detail ? $event.detail.filter(x => x.isSelected && this.lastSelectedItemIds.indexOf(x.id) == -1) : [];
        newItems.forEach(x => {
            if (this.statusSnippet && !this.statusSnippet.endsWith(", "))
                this.statusSnippet += ", ";
            else if (!this.statusSnippet) {
                this.statusSnippet = "";
            }

            if (x && x.name)
                this.statusSnippet += x.name;

            this.lastSelectedItemIds.push(x.id);
        });
    }
}
